import React, { useState } from "react";

import { faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { SchemaTypes } from "src/api/flowTypes";
import { Icon } from "src/base-components/Icon";
import { SCHEMA_TYPE_ICONS } from "src/base-components/TypeIcons";
import { Tooltip } from "src/design-system/Tooltip";
import { isObjectWithProperties } from "src/jsonSchema/types";
import { ComplexSchemaObjectPopover } from "src/parentFlowNodes/ComplexSchemaObject";
import { SchemaOptions } from "src/router/SearchParams";
import {
  getTreeSchemaWithProperty,
  hasJSONTreePreview,
  isJsonRefPath,
} from "src/schema/jsonRefUtils";
import { PropertyUI } from "src/schema/schemaMappingUtils";

type Props = {
  dataLoc?: string;
  name: string;
  type: SchemaTypes;
  sensitive: boolean;
  nullable: boolean;
  required: boolean;
  schemaTabUrl?: string;
  tooltipBody?: string;
  field?: PropertyUI;
  schemaOption?: SchemaOptions;
};

export const SchemaField: React.FC<Props> = ({
  dataLoc,
  name,
  type,
  sensitive,
  nullable,
  required,
  schemaTabUrl = "",
  tooltipBody = "Modify the input and output fields on the Decision Flow canvas.",
  field,
  schemaOption = SchemaOptions.Input,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isJsonPath = isJsonRefPath(type);
  const schema = field ? getTreeSchemaWithProperty(field) : undefined;
  const showComplexSchemaObjectPopover =
    schema &&
    (isJsonPath ||
      (field?._schema &&
        isObjectWithProperties(field._schema) &&
        hasJSONTreePreview(field, schemaOption)));

  return (
    <div
      className="flex h-8 w-full flex-1 flex-grow items-center gap-x-1 rounded-lg border border-gray-200 bg-gray-50 text-gray-500"
      data-loc={dataLoc}
    >
      <Tooltip placement="bottom" title={type} asChild>
        <div className="self-center py-1 pl-3">
          <Icon
            dataLoc={dataLoc && `${dataLoc}-type`}
            icon={
              isJsonPath ? SCHEMA_TYPE_ICONS.entity : SCHEMA_TYPE_ICONS[type]
            }
            size="xs"
          />
        </div>
      </Tooltip>
      <Tooltip
        body={tooltipBody}
        footerAction={
          schemaTabUrl
            ? {
                text: "Open Schema",
                onClick: () => window.open(schemaTabUrl, "_blank"),
              }
            : undefined
        }
        placement={isOpen ? "top" : "right"}
        title="This field is not editable"
        asChild
      >
        <div className="flex h-full w-full items-center gap-x-1 truncate py-1">
          <span
            className="font-inter-normal-12px"
            data-loc={dataLoc && `${dataLoc}-name`}
          >
            {name}
          </span>
          {required && (
            <span className="font-inter-medium-12px text-indigo-400">*</span>
          )}
        </div>
      </Tooltip>
      <div
        className={twJoin(
          "ml-auto flex items-center gap-x-1",
          !showComplexSchemaObjectPopover && "pr-2",
        )}
      >
        {nullable && (
          <Tooltip placement="bottom-end" title="Nullable" asChild>
            <span className="font-fira-code text-gray-500">null</span>
          </Tooltip>
        )}
        {sensitive && (
          <Tooltip placement="bottom-end" title="Contains PII" asChild>
            <Icon color="text-gray-500" icon={faEyeSlash} size="xs" />
          </Tooltip>
        )}
      </div>
      {showComplexSchemaObjectPopover && (
        <div className="self-center py-1 pr-2">
          <ComplexSchemaObjectPopover
            isOpen={isOpen}
            name={name}
            schema={schema}
            onClose={() => setIsOpen(false)}
            onOpen={() => setIsOpen(!isOpen)}
          />
        </div>
      )}
    </div>
  );
};
